// =============================================================================
// staticSite/assetCollector — obrazy: inwentarz, mapa src -> sciezka lokalna,
// przepisanie URL-i w HTML (TYLKO atrybuty obrazkowe: src / srcset / data-src /
// poster). NIGDY nie dotyka <a href> — linki (w tym money links) zostaja bajt w bajt.
//
// Reguly sciezek:
//   - ta sama domena (/wp-content/uploads/...) -> TA SAMA sciezka (parity URL obrazow)
//   - host zewnetrzny (fal.media, storage Supabase) -> /wp-content/uploads/static/<hash12>.<ext>
// Bajty pobiera fetcher (Edge Function fetch-asset) — regexy, zero DOM.
// =============================================================================

import { sha256 } from "@noble/hashes/sha256";
import { bytesToHex, utf8ToBytes } from "@noble/hashes/utils";
import type { AssetRef, RenderedFile, SiteModel } from "./types";
import { hostOf, isSiteHost, normalizePath } from "./urlPolicy";

const IMG_TAG_RE = /<(img|source|video|audio)\b[^>]*>/gi;
const ATTR_RE = /\b(src|srcset|data-src|data-srcset|poster)\s*=\s*("([^"]*)"|'([^']*)')/gi;

const EXT_BY_MIME: Record<string, string> = {
  "image/jpeg": ".jpg",
  "image/png": ".png",
  "image/webp": ".webp",
  "image/gif": ".gif",
  "image/svg+xml": ".svg",
  "image/avif": ".avif",
};

export function contentTypeForPath(path: string): string {
  const ext = (path.split(".").pop() || "").toLowerCase();
  const map: Record<string, string> = {
    jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png", webp: "image/webp", gif: "image/gif",
    svg: "image/svg+xml", avif: "image/avif", ico: "image/x-icon", css: "text/css", js: "text/javascript",
    html: "text/html", xml: "application/xml", txt: "text/plain", json: "application/json",
    webmanifest: "application/manifest+json", mp4: "video/mp4", webm: "video/webm", pdf: "application/pdf",
  };
  return map[ext] || "application/octet-stream";
}

/** Absolutny URL z (byc moze) wzglednego src. */
export function absolutizeSrc(src: string, baseUrl: string): string | null {
  const s = (src || "").trim();
  if (!s || s.startsWith("data:") || s.startsWith("blob:")) return null;
  try {
    if (s.startsWith("//")) return `https:${s}`;
    if (/^https?:\/\//i.test(s)) return s;
    return new URL(s, `${baseUrl.replace(/\/$/, "")}/`).toString();
  } catch {
    return null;
  }
}

/** srcset -> lista URL-i (bez deskryptorow 1x/300w). */
export function parseSrcset(value: string): string[] {
  return value
    .split(",")
    .map((part) => part.trim().split(/\s+/)[0])
    .filter(Boolean);
}

function extFromUrl(url: string): string {
  try {
    const p = new URL(url).pathname;
    const m = p.match(/\.([a-z0-9]{2,5})$/i);
    if (m) return `.${m[1].toLowerCase()}`;
  } catch {
    // ignore
  }
  return "";
}

/** Docelowa sciezka lokalna dla src (albo null gdy zostawiamy oryginal). */
export function localPathFor(src: string, site: SiteModel): { path: string; origin: AssetRef["origin"] } | null {
  const host = hostOf(src);
  if (!host) return null;
  if (isSiteHost(host, site.domain)) {
    const path = normalizePath(new URL(src).pathname);
    return { path: path.endsWith("/") ? path.slice(0, -1) : path, origin: "same_host" };
  }
  if (site.settings.rehost_external_images === false) return null;
  const hash = bytesToHex(sha256(utf8ToBytes(src))).slice(0, 12);
  const ext = extFromUrl(src) || ".jpg";
  return { path: `/wp-content/uploads/static/${hash}${ext}`, origin: "external" };
}

/** Wszystkie URL-e obrazow w HTML (src/srcset/data-src/poster z img/source/video/audio). */
export function extractImageUrls(html: string, baseUrl: string): string[] {
  const out = new Set<string>();
  for (const tag of html.match(IMG_TAG_RE) || []) {
    ATTR_RE.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = ATTR_RE.exec(tag))) {
      const attr = m[1].toLowerCase();
      const val = m[3] ?? m[4] ?? "";
      const urls = attr.endsWith("srcset") ? parseSrcset(val) : [val];
      for (const u of urls) {
        const abs = absolutizeSrc(u, baseUrl);
        if (abs) out.add(abs);
      }
    }
  }
  return [...out];
}

/** Inwentarz obrazow calej strony (tresci + featured + foto autora + logo). */
export function collectAssets(site: SiteModel): AssetRef[] {
  const byPath = new Map<string, AssetRef>();
  const add = (src: string | null | undefined, usedBy: string) => {
    if (!src) return;
    const abs = absolutizeSrc(src, site.baseUrl);
    if (!abs) return;
    const target = localPathFor(abs, site);
    if (!target) return;
    const cur = byPath.get(abs);
    if (cur) {
      if (!cur.usedBy.includes(usedBy)) cur.usedBy.push(usedBy);
      return;
    }
    byPath.set(abs, { src: abs, path: target.path, origin: target.origin, usedBy: [usedBy] });
  };
  for (const page of site.pages) {
    add(page.featuredImageUrl, page.path);
    for (const u of extractImageUrls(page.bodyHtml, site.baseUrl)) add(u, page.path);
  }
  if (site.author?.photoUrl) add(site.author.photoUrl, "author");
  return [...byPath.values()];
}

export function buildAssetMap(refs: AssetRef[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const r of refs) map.set(r.src, r.path);
  return map;
}

/**
 * Przepisuje URL-e obrazow w HTML wg mapy. Dziala TYLKO wewnatrz tagow
 * img/source/video/audio i tylko na atrybutach obrazkowych. <a href> nietkniete.
 */
export function rewriteAssetUrls(html: string, assetMap: Map<string, string>, baseUrl: string): string {
  if (!assetMap.size) return html;
  return html.replace(IMG_TAG_RE, (tag) =>
    tag.replace(ATTR_RE, (full, attr: string, _q: string, dq?: string, sq?: string) => {
      const val = dq ?? sq ?? "";
      const quote = dq !== undefined ? '"' : "'";
      const mapOne = (u: string) => {
        const abs = absolutizeSrc(u, baseUrl);
        return (abs && assetMap.get(abs)) || u;
      };
      let next: string;
      if (attr.toLowerCase().endsWith("srcset")) {
        next = val
          .split(",")
          .map((part) => {
            const t = part.trim();
            if (!t) return t;
            const [u, ...rest] = t.split(/\s+/);
            return [mapOne(u), ...rest].join(" ");
          })
          .join(", ");
      } else {
        next = mapOne(val);
      }
      return next === val ? full : `${attr}=${quote}${next}${quote}`;
    }),
  );
}

export interface AssetFetcher {
  (url: string): Promise<{ bytes: Uint8Array; contentType: string }>;
}

export interface ResolvedAssetFile extends RenderedFile {
  /** Oryginalny URL (do mapy src -> sciezka; sciezka moze dostac rozszerzenie z content-type). */
  src: string;
}

export interface ResolveAssetsResult {
  files: ResolvedAssetFile[];
  failed: Array<{ src: string; path: string; error: string }>;
}

/**
 * Pobiera bajty obrazow (fetcher = Edge Function). Pomija te, ktore sa juz
 * w manifescie z poprzedniego deployu (dedup — CF i tak ma je w KV).
 */
export async function resolveAssets(
  refs: AssetRef[],
  fetcher: AssetFetcher,
  onProgress?: (current: number, total: number, path: string) => void,
  concurrency = 4,
): Promise<ResolveAssetsResult> {
  const files: ResolvedAssetFile[] = [];
  const failed: ResolveAssetsResult["failed"] = [];
  let idx = 0;
  let done = 0;
  const worker = async () => {
    while (idx < refs.length) {
      const ref = refs[idx++];
      try {
        const { bytes, contentType } = await fetcher(ref.src);
        let path = ref.path;
        // Host zewnetrzny bez rozszerzenia w URL -> dopasuj po content-type
        if (ref.origin === "external" && !/\.[a-z0-9]{2,5}$/i.test(path)) {
          path += EXT_BY_MIME[contentType] || ".jpg";
        }
        files.push({ src: ref.src, path, bytes, contentType: contentType || contentTypeForPath(path) });
      } catch (e) {
        failed.push({ src: ref.src, path: ref.path, error: e instanceof Error ? e.message : String(e) });
      } finally {
        done++;
        onProgress?.(done, refs.length, ref.path);
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, Math.max(1, refs.length)) }, worker));
  return { files, failed };
}
